/*
 * Copyright © 2017-2020 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";
import { ScrollView, View } from "react-native";
import Rodal from "rodal";
import Icon from "react-icons-kit";
import { ic_close } from "react-icons-kit/md/ic_close";
import { SyncLoader } from "react-spinners";
import ReactTooltip from "react-tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useSelector } from "react-redux";
import { RootState } from "store/types";
import { callApi } from "config/UpduoService";

import ErrorRow from "./ErrorRow";
import { Button, Text } from "UpduoLibrary";
import {
  skaleDarkGray,
  skaleWebBorderGray,
  skalePrimaryBlue,
  skaleRed,
  skaleGreen,
  skaleLinkBlue,
  skaleWhite,
} from "config/UpduoColors";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function DataModal({ dataInfo, onClose, onDelete }) {
  const [modalHeight] = useState(550);
  const modalWidth = modalHeight;

  const selfObject = useSelector((state: RootState) => state.selfObject);
  const fileInfoObject = useSelector(
    (state: RootState) => state.fileInfoObject
  );
  const [isUploadButtonPressed, setUploadButtonPressed] = useState(false);
  const [columnHeaderArray, setColumnHeaderArray] = useState<string[]>([]);
  const [csvUploadResults, setCsvUploadResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const useStyles = makeStyles({
    table: {
      minWidth: 650,
      height: modalHeight,
    },
    tablecell: {
      width: 100,
      minWidth: 100,
    },
    tableWrapper: {
      height: modalHeight + 100,
      overflow: "auto",
    },
  });
  const classes = useStyles();

  const _handleResetUploadState = () => {
    setCsvUploadResults({});
    setUploadButtonPressed(false);
  };

  useEffect(() => {
    if (dataInfo && dataInfo.length > 0) {
      const columnsIn = dataInfo[0];
      const newColumnHeaderArray: string[] = [];
      for (const key in columnsIn) {
        newColumnHeaderArray.push(key);
      }
      setColumnHeaderArray(newColumnHeaderArray);
    }
  }, [dataInfo]);

  const row = useCallback(
    (rowObject, rowIndex) => (
      <TableRow key={rowIndex}>
        {columnHeaderArray.map((columnString, columnIndex) => (
          <TableCell key={columnIndex} className={classes.tablecell}>
            {rowObject[columnString]}
          </TableCell>
        ))}
      </TableRow>
    ),
    [columnHeaderArray, classes.tablecell]
  );

  const _handlePostMetricMetadataNew = () => {
    setIsLoading(true);
    const body = {
      file_name: fileInfoObject.file_name,
      row_count: fileInfoObject.row_count,
      column_count: fileInfoObject.column_count,
      data: dataInfo,
    };
    callApi(
      "postMetricMetadataNew",
      body,
      (responseJson) => {
        const csvUploadResults = responseJson;
        setCsvUploadResults(csvUploadResults);
        setIsLoading(false);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const _handleDeleteMetricMetadata = () => {
    callApi("deleteMetricMetadataNew", undefined, undefined, (error) => {
      console.log(error);
    });
  };

  const choiceButtons = (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "flex-end",
      }}
    >
      <View>
        <Button
          onPress={() => {
            UpduoAnalytics.logEvent("user-upload-csv-performance-data-done", {
              userName: `${selfObject.lastName} ${selfObject.lastName}`,
              userId: selfObject.userId,
              organizationName: selfObject.organizationName,
              organizationId: selfObject.organizationId,
            });
            onClose();
            _handleResetUploadState();
          }}
          width={150}
          text={"Done"}
          height={36}
          borderWidth={1}
          borderColor={skalePrimaryBlue}
          color={skaleWhite}
          textColor={skalePrimaryBlue}
        />
      </View>
      <View
        style={{
          marginLeft: 20,
          marginRight: 20,
        }}
      >
        <View>
          <span data-tip data-for={"cancel-button"}>
            <Button
              onPress={() => {
                UpduoAnalytics.logEvent(
                  "user-upload-csv-performance-data-delete",
                  {
                    userName: `${selfObject.lastName} ${selfObject.lastName}`,
                    userId: selfObject.userId,
                    organizationName: selfObject.organizationName,
                    organizationId: selfObject.organizationId,
                  }
                );
                _handleDeleteMetricMetadata();
                _handleResetUploadState();
                onDelete();
              }}
              width={200}
              text={"Cancel Upload"}
              height={36}
              borderWidth={1}
              borderColor={skaleRed}
              color={skaleWhite}
              textColor={skaleRed}
            />
          </span>
        </View>
        <ReactTooltip id={"cancel-button"} type={"dark"} effect={"solid"}>
          <Text style={{ color: skaleWhite }}>
            {
              "Delete uploaded file and all row changes\n(including already submitted rows)"
            }
          </Text>
        </ReactTooltip>
      </View>
    </View>
  );

  const optionArray = [
    "Select user from database",
    "Save user without account",
    "Delete row",
  ];

  const loadingView = (
    <View
      style={{
        height: modalHeight + 100,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Text style={{ fontSize: 40 }}>{"File uploading..."}</Text>
      <View style={{ marginLeft: 20, marginTop: 20 }}>
        <SyncLoader color={skaleLinkBlue} size={15} />
      </View>
    </View>
  );

  const resultView = (
    <View>
      <Text
        style={{
          fontSize: 20,
          marginLeft: 10,
          marginVertical: 10,
          fontWeight: "bold",
        }}
      >
        {`File uploaded! Processed ${fileInfoObject.row_count} row(s) including header.`}
      </Text>
      {csvUploadResults["Errors"] && csvUploadResults["Errors"].length > 0 ? (
        <View style={{ margin: 10 }}>
          <Text
            style={{
              marginLeft: 15,
              fontSize: 16,
            }}
          >
            {"To fix errors:"}
          </Text>
          <Text
            style={{
              marginLeft: 15,
              fontSize: 16,
              fontWeight: "bold",
            }}
          >
            {
              "Option 1: Select options for each row, press 'Submit' for each row, then press 'Done' (RECOMMENDED OPTION) "
            }
          </Text>
          <Text
            style={{
              marginLeft: 15,
              fontSize: 16,
              marginBottom: 10,
            }}
          >
            {
              "Option 2: Cancel upload, fix the errors in the CSV, and upload the file again."
            }
          </Text>
          <Text
            style={{
              marginLeft: 15,
              fontSize: 20,
              fontWeight: "bold",
              color: skaleRed,
            }}
          >
            {`${csvUploadResults["Errors"].length} row(s) with Errors: `}
          </Text>
          <View style={{ flex: 1, flexDirection: "row", marginLeft: 15 }}>
            {optionArray.map((option, optionIndex) => {
              return (
                <View
                  key={optionIndex}
                  style={{ width: 100, marginHorizontal: 2, marginBottom: 10 }}
                >
                  <Text style={{ textAlign: "center", fontWeight: "bold" }}>
                    {option}
                  </Text>
                </View>
              );
            })}
          </View>
          <ScrollView style={{ marginTop: 10, height: modalHeight - 350 }}>
            {csvUploadResults["Errors"].map((item, itemIndex) => {
              return (
                <ErrorRow key={itemIndex} item={item} itemIndex={itemIndex} />
              );
            })}
          </ScrollView>
        </View>
      ) : null}
      {csvUploadResults["Created"] && csvUploadResults["Created"].length > 0 ? (
        <View style={{ marginVertical: 10, marginHorizontal: 10 }}>
          <Text
            style={{
              marginLeft: 15,
              fontSize: 20,
              fontWeight: "bold",
              color: skaleGreen,
            }}
          >
            {`${csvUploadResults["Created"].length} row(s) successfully created: `}
          </Text>
          <ScrollView style={{ height: modalHeight - 450 }}>
            {csvUploadResults["Created"].map((item, itemIndex) => {
              return (
                <Text
                  key={itemIndex}
                  style={{
                    marginLeft: 15,
                    fontSize: 16,
                  }}
                >
                  {`• ${item}`}
                </Text>
              );
            })}
          </ScrollView>
        </View>
      ) : (
        <Text
          style={{
            marginLeft: 15,
            marginTop: 10,
            fontSize: 20,
            fontWeight: "bold",
            color: skaleRed,
          }}
        >
          {"No user data uploaded."}
        </Text>
      )}
      <Text
        style={{
          marginLeft: 15,
          fontSize: 16,
          marginBottom: 10,
        }}
      >
        {
          "Note: Clicking 'Done' or exiting out from pop-up without submitting any row changes will ignore users that were not found"
        }
      </Text>
      {choiceButtons}
    </View>
  );

  const resultsPage = isLoading ? loadingView : resultView;

  return (
    <Rodal
      visible={dataInfo && dataInfo.length > 0}
      customStyles={{
        marginTop: window.innerHeight * 0.06,
        width: `${Math.max((7 * modalWidth) / 3, 1000)}px`,
        height: 700,
      }}
      customMaskStyles={{ marginTop: -100, height: "250%" }}
      onClose={() => {
        UpduoAnalytics.logEvent("user-data-modal-exit");
        onClose();
        _handleResetUploadState();
      }}
    >
      {!isUploadButtonPressed ? (
        <View style={{ height: modalHeight + 100 }}>
          <Text style={{ marginVertical: 10, fontSize: 18 }}>
            {"Confirm uploaded data:"}
          </Text>
          <Text style={{ marginVertical: 10, fontSize: 14 }}>
            {`First column '${columnHeaderArray[0]}' will be used to find members in the database. Make sure it contains a name or an email.`}
          </Text>
          <ScrollView style={{ marginTop: 10, height: modalHeight - 250 }}>
            <TableContainer
              component={Paper}
              style={{
                color: "blue",
                borderColor: skaleWebBorderGray,
                borderWidth: 5,
              }}
            >
              <Table
                className={classes.table}
                size={"small"}
                aria-label={"a dense table"}
                stickyHeader
              >
                <TableHead>
                  <TableRow>
                    {columnHeaderArray.map((item, itemIndex) => (
                      <TableCell
                        key={itemIndex + 1}
                        className={classes.tablecell}
                        style={{
                          fontWeight: "bold",
                        }}
                      >
                        {item}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {dataInfo && dataInfo.length > 0 && columnHeaderArray ? (
                  <TableBody>
                    {dataInfo.map((item, itemIndex) => row(item, itemIndex))}
                  </TableBody>
                ) : null}
              </Table>
            </TableContainer>
          </ScrollView>
          <Text style={{ marginVertical: 10, fontSize: 14 }}>
            {`Row count (including header): ${fileInfoObject.row_count}`}
            {`\nColumn count: ${fileInfoObject.column_count}`}
          </Text>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              marginRight: 50,
            }}
          >
            <Button
              onPress={() => {
                UpduoAnalytics.logEvent(
                  "user-upload-csv-performance-data-confirm",
                  {
                    userName: `${selfObject.lastName} ${selfObject.lastName}`,
                    userId: selfObject.userId,
                    organizationName: selfObject.organizationName,
                    organizationId: selfObject.organizationId,
                  }
                );
                _handlePostMetricMetadataNew();
                setUploadButtonPressed(true);
              }}
              width={150}
              text={"Confirm"}
              height={36}
              borderWidth={1}
              borderColor={skalePrimaryBlue}
              color={skaleWhite}
              textColor={skalePrimaryBlue}
            />
          </View>
        </View>
      ) : (
        resultsPage
      )}
      <View style={{ position: "absolute", top: 14, right: 14 }}>
        <div style={{ color: skaleDarkGray }}>
          <Icon size={20} icon={ic_close} />
        </div>
      </View>
    </Rodal>
  );
}
